/// <reference types="vite/client" />

import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaV3Provider } from "firebase/app-check";
import { getAuth } from "firebase/auth";
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from "firebase/firestore";

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);

// Verifies that requests hitting Firestore/Cloud Functions actually come
// from this app, not a script calling your (necessarily public) API key
// directly — closes the gap where anyone could copy the key out of the
// bundle and hit your Blaze-billed backend on their own. Gated on an env
// var rather than always-on: activating it for real also requires a
// reCAPTCHA v3 site key (from google.com/recaptcha/admin, tied to your
// domain) and flipping Firestore/Functions to "Enforced" in the Firebase
// Console under App Check — both manual, account-linked steps that can't
// be scripted. Until VITE_RECAPTCHA_SITE_KEY is set, this is a no-op and
// the app behaves exactly as it does today.
if (import.meta.env.VITE_RECAPTCHA_SITE_KEY) {
  initializeAppCheck(app, {
    provider: new ReCaptchaV3Provider(import.meta.env.VITE_RECAPTCHA_SITE_KEY),
    isTokenAutoRefreshEnabled: true,
  });
}

export const auth = getAuth(app);
// Persistent IndexedDB cache: repeat reads of data we've already fetched
// (match history, leaderboards, etc.) are served locally instead of a fresh
// network round-trip every time, and Firestore syncs deltas in the
// background. persistentMultipleTabManager avoids the cache silently
// disabling itself when the app is open in more than one tab at once.
export const db = initializeFirestore(app, {
  localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
});
// firebase/functions is only used by Battle.jsx (ranked) and Custom.jsx
// (private lobbies) via httpsCallable — but firebase.ts itself is imported
// by every page (auth/db are needed everywhere), so importing it eagerly
// here shipped its weight to visitors who only ever play Freeplay or VS AI
// and never call a single Cloud Function. Same deferred-import pattern as
// analytics below.
let functionsModulePromise = null;
export function getFunctionsInstance() {
  if (!functionsModulePromise) {
    functionsModulePromise = import("firebase/functions").then((mod) => mod.getFunctions(app));
  }
  return functionsModulePromise;
}

// `firebase/analytics` is only ever used for page_view logging — not part
// of the app's critical path — but it's heavy enough that importing it
// eagerly grows the initial bundle every visitor downloads, including ones
// who never trigger anything analytics-related. Load it on demand instead.
let analyticsModulePromise = null;
function loadAnalytics() {
  if (!analyticsModulePromise) {
    analyticsModulePromise = import("firebase/analytics").then((mod) => ({
      logEvent: mod.logEvent,
      instance: mod.getAnalytics(app),
    }));
  }
  return analyticsModulePromise;
}

export function trackPageView(pagePath) {
  loadAnalytics()
    .then(({ logEvent, instance }) => logEvent(instance, "page_view", { page_path: pagePath }))
    .catch(() => {});
}

export default app;